/**
 * Controller Page
 * Phone-based remote control for kiosk player
 * Accessed via QR code scan from Player
 */

import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useControllerWebSocket, type PlaylistItem } from '../hooks/useRoomWebSocket';

interface Playlist {
  id: string;
  name: string;
  itemCount: number;
}

export default function Controller() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const [userId] = useState(() => crypto.randomUUID());
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [playlistItems, setPlaylistItems] = useState<PlaylistItem[]>([]);
  const [showPlaylistSelector, setShowPlaylistSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // WebSocket connection
  const {
    isConnected,
    isPaired,
    roomState,
    play,
    pause,
    next,
    prev,
    setPlaylist,
  } = useControllerWebSocket({
    roomCode: roomCode || '',
    userId,
    onError: setError,
  });

  // Fetch available playlists
  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const response = await fetch('/api/v1/playlists', {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.data) {
          setPlaylists(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch playlists:', err);
      }
    };

    if (isConnected) {
      fetchPlaylists();
    }
  }, [isConnected]);

  // Fetch current playlist items when playlistId changes
  useEffect(() => {
    if (!roomState?.playlistId) {
      setPlaylistItems([]);
      return;
    }

    const fetchPlaylistItems = async () => {
      try {
        const response = await fetch(`/api/v1/playlists/${roomState.playlistId}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success && data.data?.items) {
          setPlaylistItems(data.data.items);
        }
      } catch (err) {
        console.error('Failed to fetch playlist items:', err);
      }
    };

    fetchPlaylistItems();
  }, [roomState?.playlistId]);

  // Find current item in playlist
  const currentItem = useMemo(() => {
    if (!roomState?.itemId || playlistItems.length === 0) return null;
    return playlistItems.find((item) => item.id === roomState.itemId) || playlistItems[0];
  }, [roomState?.itemId, playlistItems]);

  // Current playlist info
  const currentPlaylist = useMemo(() => {
    if (!roomState?.playlistId) return null;
    return playlists.find((p) => p.id === roomState.playlistId);
  }, [roomState?.playlistId, playlists]);

  // Handle playlist selection
  const handleSelectPlaylist = (playlistId: string) => {
    setPlaylist(playlistId);
    setShowPlaylistSelector(false);
  };

  // Format progress
  const formatProgress = () => {
    if (!playlistItems.length) return '0 / 0';
    const currentIndex = currentItem
      ? playlistItems.findIndex((item) => item.id === currentItem.id) + 1
      : 0;
    return `${currentIndex} / ${playlistItems.length}`;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">Gallery Controller</h1>
            <p className="text-white/50 text-sm font-mono">{roomCode}</p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                isConnected ? (isPaired ? 'bg-green-500' : 'bg-yellow-500') : 'bg-red-500'
              }`}
            />
            <span className="text-white/50 text-sm">
              {isConnected ? (isPaired ? 'Paired' : 'Connecting...') : 'Offline'}
            </span>
          </div>
        </div>
      </header>

      {/* Error display */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-300 text-xs underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Now Playing Card */}
      <div className="p-4">
        <div className="bg-white/5 rounded-2xl p-5">
          {currentItem ? (
            <>
              {/* Thumbnail */}
              {currentItem.thumbnailUrl && (
                <div className="aspect-video bg-white/10 rounded-xl mb-4 overflow-hidden">
                  <img
                    src={currentItem.thumbnailUrl}
                    alt={currentItem.artworkTitle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Item info */}
              <div className="mb-4">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Now Playing</p>
                <h2 className="text-xl font-semibold">{currentItem.artworkTitle}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-white/70 text-sm">{formatProgress()}</span>
                  <span className="text-white/30">•</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      currentItem.artworkType === 'video'
                        ? 'bg-purple-500/20 text-purple-300'
                        : 'bg-blue-500/20 text-blue-300'
                    }`}
                  >
                    {currentItem.artworkType}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-white/50">No playlist selected</p>
              <button
                onClick={() => setShowPlaylistSelector(true)}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors"
              >
                Select Playlist
              </button>
            </div>
          )}

          {/* Playlist info */}
          {currentPlaylist && (
            <button
              onClick={() => setShowPlaylistSelector(true)}
              className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
            >
              <div className="text-left">
                <p className="text-white/50 text-xs">Playlist</p>
                <p className="font-medium">{currentPlaylist.name}</p>
              </div>
              <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main Controls */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="flex items-center gap-6">
          {/* Previous */}
          <button
            onClick={prev}
            disabled={!isConnected}
            className="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all touch-manipulation"
          >
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z" />
            </svg>
          </button>

          {/* Play/Pause */}
          <button
            onClick={roomState?.isPaused ? play : pause}
            disabled={!isConnected}
            className="w-24 h-24 rounded-full bg-white hover:bg-white/90 active:bg-white/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all touch-manipulation"
          >
            {roomState?.isPaused ? (
              <svg className="w-12 h-12 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg className="w-12 h-12 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            )}
          </button>

          {/* Next */}
          <button
            onClick={next}
            disabled={!isConnected}
            className="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all touch-manipulation"
          >
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
        </div>

        {/* Status indicator */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            {roomState?.isPaused ? 'Paused' : 'Playing'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-4 border-t border-white/10">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setShowPlaylistSelector(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <span className="text-sm">Playlists</span>
          </button>
        </div>
      </footer>

      {/* Playlist Selector Modal */}
      {showPlaylistSelector && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
          <div className="w-full bg-neutral-900 rounded-t-3xl max-h-[80vh] flex flex-col">
            {/* Modal header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Select Playlist</h3>
              <button
                onClick={() => setShowPlaylistSelector(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Playlist list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {playlists.length === 0 ? (
                <p className="text-center text-white/50 py-8">No playlists available</p>
              ) : (
                playlists.map((playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => handleSelectPlaylist(playlist.id)}
                    className={`w-full text-left p-4 rounded-xl transition-colors ${
                      roomState?.playlistId === playlist.id
                        ? 'bg-white/20'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{playlist.name}</p>
                        <p className="text-white/50 text-sm">{playlist.itemCount} items</p>
                      </div>
                      {roomState?.playlistId === playlist.id && (
                        <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
